import { RefSelector } from "./componentAnnotations";
import { DyInputRange } from "./dyInputRange";
import { DyAbstractLabel, LabelAlignment, IDyLabel } from "./dyAbstractLabel";
import { DyInputNumberField } from "./dyInputNumberField";
import { DyAbstractField } from "./dyAbstractField";
import { PostConstruct } from "../context/context";
import { addCssClass } from "../utils/dom";

export class DySlider extends DyAbstractLabel {
  private static TEMPLATE = /* html */
    `<div class="dy-slider">
            <label ref="eLabel"></label>
            <div class="dy-wrapper dy-slider-wrapper">
                <dy-input-range ref="eSlider"></dy-input-range>
                <dy-input-number-field ref="eText"></dy-input-number-field>
            </div>
        </div>`;

  @RefSelector('eLabel') protected readonly eLabel: HTMLElement;
  @RefSelector('eSlider') private readonly eSlider: DyInputRange;
  @RefSelector('eText') private readonly eText: DyInputNumberField;

  protected labelAlignment: LabelAlignment = 'top';

  constructor(config?: IDyLabel) {
    super(config, DySlider.TEMPLATE);
  }

  @PostConstruct
  private init() {
    addCssClass(this.eSlider.getGui(), 'dy-slider-field');
  }

  public onValueChange(callbackFn: (newValue: number) => void) {
    const eventChanged = DyAbstractField.EVENT_CHANGED;
    this.addManagedListener(this.eText, eventChanged, () => {
      const textValue = parseFloat(this.eText.getValue()!);
      this.eSlider.setValue(textValue.toString(), true);
      callbackFn(textValue || 0);
    });

    this.addManagedListener(this.eSlider, eventChanged, () => {
      const sliderValue = this.eSlider.getValue()!;
      this.eText.setValue(sliderValue, true);
      callbackFn(parseFloat(sliderValue));
    });

    return this;
  }

  public setSliderWidth(width: number): this {
    this.eSlider.setWidth(width);
    return this;
  }

  public setTextFieldWidth(width: number): this {
    this.eText.setWidth(width);
    return this;
  }

  public setMinValue(minValue: number): this {
    this.eSlider.setMinValue(minValue);
    this.eText.setMin(minValue);

    return this;
  }

  public setMaxValue(maxValue: number): this {
    this.eSlider.setMaxValue(maxValue);
    this.eText.setMax(maxValue);
    return this;
  }

  public getValue(): string | null | undefined {
    return this.eText.getValue();
  }

  public setValue(value: string): this {
    if (this.getValue() === value) {
      return this;
    }

    this.eText.setValue(value, true);
    this.eSlider.setValue(value, true);

    this.dispatchEvent({ type: DyAbstractField.EVENT_CHANGED });

    return this;
  }

  public setStep(step: number): this {
    this.eSlider.setStep(step);
    this.eText.setStep(step);
    return this;
  }
}
